import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Listings.css";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

// function CountUp({ to = 0, duration = 800, prefix = "" }) {
//   const [val, setVal] = useState(0);
//   const start = useRef(null);
//   const target = Number(to || 0);

//   useEffect(() => {
//     start.current = null;
//     let raf = 0;
//     const step = (ts) => {
//       if (!start.current) start.current = ts;
//       const p = Math.min(1, (ts - start.current) / duration);
//       const eased = 1 - Math.pow(1 - p, 3);
//       setVal(target * eased);
//       if (p < 1) raf = requestAnimationFrame(step);
//     };
//     raf = requestAnimationFrame(step);
//     return () => cancelAnimationFrame(raf);
//   }, [target, duration]);

//   return <>{prefix}{Math.round(val).toLocaleString()}</>;
// }

function StatusPill({ status, onClick }) {
  const cls =
    status === "Active" ? "badge green as-button"
      : (status || "").toLowerCase() === "pending" ? "badge amber as-button"
        : "badge gray as-button";
  const label = status === "Active" ? "Active" : "Pending";
  return (
    <button className={cls} onClick={onClick} title="Click to toggle Pending/Active">
      {label}
    </button>
  );
}

export default function HotelListings({ onStatusChange }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    currency: '',
    minPrice: '',
    maxPrice: '',
    category: ''
  });
  const [statuses, setStatuses] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [categories] = useState(['Hotel']);
  const [hotels, setHotels] = useState([]);

  useEffect(() => {
    async function fetchInitialData() {
      try {
        const [statusesRes, currenciesRes] = await Promise.all([
          fetch(`${API}/statuses`),
          fetch(`${API}/hotels/currencies`)
        ]);
        const statusesData = await statusesRes.json();
        const currenciesData = await currenciesRes.json();
        if (Array.isArray(statusesData)) setStatuses(statusesData);
        if (Array.isArray(currenciesData)) setCurrencies(currenciesData);
      } catch (e) {
        console.error("Failed to fetch initial data", e);
      }
    }
    fetchInitialData();
  }, []);

  useEffect(() => {
    let mounted = true;
    async function loadHotels() {
      try {
        setLoading(true);
        setErr("");
        const cleanParams = new URLSearchParams();
        Object.entries(filters).forEach(([k, v]) => {
          if (v !== '' && v !== null && v !== undefined) {
            cleanParams.append(k, v);
          }
        });
        const query = cleanParams.toString();
        const url = `${API}/admin/listings/hotels${query ? `?${query}` : ''}`;
        const res = await fetch(url);
        const json = await res.json();
        if (!res.ok) throw new Error(json?.message || "Failed to fetch hotel listings.");
        if (mounted) {
          setHotels(Array.isArray(json) ? json : []);
        }
      } catch (e) {
        if (mounted) setErr(e.message || "Load failed");
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadHotels();
    return () => { mounted = false; };
  }, [filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      currency: '',
      minPrice: '',
      maxPrice: '',
      category: ''
    });
  };

  async function toggleStatusHotel(listingId) {
    try {
      setHotels((prev) =>
        prev.map((h) =>
          h.listingId === listingId
            ? { ...h, status: h.status === "Active" ? "pending" : "Active" }
            : h
        )
      );

      const res = await fetch(`${API}/admin/listings/${listingId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to update status.");

      if (data.row) {
        setHotels((prev) =>
          prev.map((h) => (h.listingId === listingId ? { ...h, ...data.row } : h))
        );
      }
      if (onStatusChange) {
        onStatusChange(data.summary);
      }
    } catch (e) {
      alert(e.message || "Update failed.");
      // Simple reload on failure
      const res = await fetch(`${API}/admin/listings/hotels`);
      const json = await res.json();
      if (res.ok) setHotels(Array.isArray(json) ? json : []);
    }
  }

  const priceLabel = (roomType, price) => {
    const rt = (roomType || "").toString().trim();
    const nice = rt ? `${rt.charAt(0).toUpperCase()}${rt.slice(1).toLowerCase()}` : "N/A";
    return <span>{nice} Room<br />LKR {Number(price || 0).toLocaleString()}</span>;
  };

  return (
    <section className={`card table-card ${loading ? 'loading' : ''}`}>
      <div className="table-head">
        <h3 className="table-title">Hotel Listings</h3>
      </div>
      <div className="filters">
        <input type="text" name="search" placeholder="Search by ID, Name, Location, Contact" value={filters.search} onChange={handleFilterChange} />
        <select name="status" value={filters.status} onChange={handleFilterChange} className={`input ${filters.status ? "filter-selected" : "filter-muted"}`}>
          <option value="">All Statuses</option>
          {statuses.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
        </select>
        <select name="currency" value={filters.currency} onChange={handleFilterChange} className={`input ${filters.currency ? "filter-selected" : "filter-muted"}`}>
          <option value="">All Currencies</option>
          {currencies.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <select name="category" value={filters.category} onChange={handleFilterChange} className={`input ${filters.category ? "filter-selected" : "filter-muted"}`}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <input type="number" name="minPrice" placeholder="Min Price" value={filters.minPrice} onChange={handleFilterChange} />
        <input type="number" name="maxPrice" placeholder="Max Price" value={filters.maxPrice} onChange={handleFilterChange} />
        <button onClick={handleClearFilters}>Clear All</button>
      </div>
      <br />
      {err && <div style={{ color: "crimson", padding: '10px 20px' }}>{err}</div>}
      <table className="table">
        <thead>
          <tr>
            <th style={{ width: 34 }}><input type="checkbox" aria-label="select all" /></th>
            <th>Listing ID</th>
            <th>Hotel Name</th>
            <th>Category</th>
            <th>Description</th>
            <th>Location</th>
            <th>Contact info</th>
            <th>Price List</th>
            <th>Currency</th>
            <th style={{ width: 120 }}>Status</th>
            <th style={{ width: 100 }}>Hold</th>
            <th style={{ width: 120 }}>Details</th>
          </tr>
        </thead>
        <tbody>
          {hotels.map((h, index) => (
            <tr key={`${h.listingId}-${index}`}>
              <td><input type="checkbox" aria-label={`select ${h.listId}`} /></td>
              <td><span className="id-badge">{h.listId}</span></td>
              <td>{h.hotelName}</td>
              <td>Hotel</td>
              <td className="muted">{h.description}</td>
              <td className="muted">{h.location}</td>
              <td className="muted">
                <div className="mono">{h.contact?.phone || "—"}</div>
                <div className="mono">{h.contact?.email || "—"}</div>
              </td>
              <td className="muted">
                {priceLabel(h.roomType, h.price)}
              </td>
              <td className="muted">{h.currency}</td>
              <td>
                <StatusPill status={h.status} onClick={() => toggleStatusHotel(h.listingId)} />
              </td>
              <td>
                <button
                  className={`badge ${h.status === "Active" ? "amber" : "green"} as-button`}
                  onClick={() => toggleStatusHotel(h.listingId)}
                >
                  {h.status === "Active" ? "Hold" : "Unhold"}
                </button>
              </td>
              <td>
                <button
                  className="badge gray as-button"
                  onClick={() => {
                    if (h.listingId) {
                      navigate(`/admin/listings/hotels/${h.listingId}`);
                    } else {
                      alert("Listing details not found.");
                    }
                  }}
                >
                  View Details
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
